import { Configuration, AdminCompanySearchDto, AdminCompanyProductConfigsDto, ProfileSearchDto, AdminApi, UserAuthResponse } from "@inosio/payments-sdk";
import { AxiosResponse } from "axios";
import { call, put, select, takeLatest } from "redux-saga/effects";
import { Stream } from "stream";
import { IpaUserExtended, selectCurrentUser } from "../../shared/sagas/auth/authSlice";
import { 
  getCompanies, 
  setCompanies, 
  setCompaniesError, 
  getCompanyDetail, 
  setCompanyDetail, 
  setCompanyDetailError, 
  getProfiles, 
  setProfiles, 
  setProfilesError,
  getExportBankFiles,
  setExportBankFiles,
  setExportBankFilesError,
  getUpdateCompanyProductConfigs,
  setUpdateCompanyProductConfigs,
  setUpdateCompanyProductConfigsError,
  submitOnboardMerchant,
  setOnboardMerchantData,
  setOnboardMerchantError,
} from "./adminPortal.slice";

const { REACT_APP_IPA_BASE_PATH } = process.env;

export function requestGetCompanies(access_token: string, searchDto: AdminCompanySearchDto) {
  const apiConfig = new Configuration({
    basePath: REACT_APP_IPA_BASE_PATH,
    accessToken: access_token,
  });
  const ipaAdminAPI = new AdminApi(apiConfig);

  return ipaAdminAPI.searchCompanies(searchDto);
}

export function requestGetProfiles(access_token: string, searchDto: ProfileSearchDto) {
  const apiConfig = new Configuration({
    basePath: REACT_APP_IPA_BASE_PATH,
    accessToken: access_token,
  });
  const ipaAdminAPI = new AdminApi(apiConfig);

  return ipaAdminAPI.searchProfiles(searchDto);
}

export function requestGetCompanyDetail(access_token: string, companyUuid: string) {
  const apiConfig = new Configuration({
    basePath: REACT_APP_IPA_BASE_PATH,
    accessToken: access_token,
  });
  const ipaAdminAPI = new AdminApi(apiConfig);

  return ipaAdminAPI.getCompanyDetail(companyUuid);
}

export function requestExportBankFiles(access_token: string, companyUuid: string, bankUuid: string) {
  const responseType = "arraybuffer";
  const contentDisposition = "application/zip";
  const headerParams = {
    "Content-Disposition": contentDisposition,
  };
  const apiConfig = new Configuration({
    basePath: REACT_APP_IPA_BASE_PATH,
    accessToken: access_token,
  });
  const ipaAdminAPI = new AdminApi(apiConfig);

  return ipaAdminAPI.exportBankAccountFiles(companyUuid, bankUuid, { responseType, headers: headerParams });
}

export function requestUpdateCompanyProductConfigs(access_token: string, companyUuid: string, updateDto: AdminCompanyProductConfigsDto) {
  const apiConfig = new Configuration({
    basePath: REACT_APP_IPA_BASE_PATH,
    accessToken: access_token,
  });
  const ipaAdminAPI = new AdminApi(apiConfig);

  return ipaAdminAPI.updateCompanyProductConfigs(companyUuid, updateDto);
}

export function requestSubmitOnboardMerchant(access_token: string, companyUuid: string) {
  const apiConfig = new Configuration({
    basePath: REACT_APP_IPA_BASE_PATH,
    accessToken: access_token,
  });
  const ipaAdminAPI = new AdminApi(apiConfig);

  return ipaAdminAPI.submitOnboardingApp(companyUuid);
}

export function* handleGetCompanies(action: { type: typeof getCompanies; payload: AdminCompanySearchDto }) {
  try {
    const currentUser:IpaUserExtended= yield select(selectCurrentUser);
    const response: AxiosResponse<UserAuthResponse> = yield call(requestGetCompanies, currentUser.accessToken, action.payload);

    const { data } = response;
    yield put(setCompanies(data));
  } catch (error: any) {
    yield put(setCompaniesError(error?.response?.data));
  }
}

export function* handleGetProfiles(action: { type: typeof getProfiles; payload: ProfileSearchDto }) {
  try {
    const currentUser:IpaUserExtended= yield select(selectCurrentUser);
    const response: AxiosResponse<UserAuthResponse> = yield call(requestGetProfiles, currentUser.accessToken, action.payload);

    const { data } = response;
    yield put(setProfiles(data));
  } catch (error: any) {
    yield put(setProfilesError(error?.response?.data));
  }
}

export function* handleGetCompanyDetail(action: any) {
  try {
    const currentUser:IpaUserExtended= yield select(selectCurrentUser);
    const response: AxiosResponse<UserAuthResponse> = yield call(requestGetCompanyDetail, currentUser.accessToken, action.payload.CompanyUuid);

    const { data } = response;
    yield put(setCompanyDetail(data));
  } catch (error: any) {
    yield put(setCompanyDetailError(error?.response?.data));
  }
}

export function* handleExportBankFiles(action: { type: typeof getExportBankFiles; payload: { CompanyUuid: string, BankUuid: string } }) {
  try {
    const currentUser:IpaUserExtended= yield select(selectCurrentUser);
    const response: AxiosResponse<Stream> = yield call(requestExportBankFiles, currentUser.accessToken, action.payload.CompanyUuid, action.payload.BankUuid);

    // download stream
    let fileName = "bank_attachments.zip"; // fallback
    const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
    const matches = filenameRegex.exec(response.headers["content-disposition"]);
    if (matches != null && matches[1]) {
      fileName = matches[1].replace(/['"]/g, "");
    }

    const url = window.URL.createObjectURL(new Blob([response.data as unknown as BlobPart], { type: "application/zip" }));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `${fileName}`);
    document.body.appendChild(link);
    link.click();
    URL.revokeObjectURL(url);

    yield put(setExportBankFiles());
  } catch (error: any) {
    yield put(setExportBankFilesError({ message: error?.response?.data?.message }));
  }
}

export function* handleUpdateCompanyProductConfigs(action: any) {
  try {
    const currentUser:IpaUserExtended= yield select(selectCurrentUser);
    const response: AxiosResponse<UserAuthResponse> = yield call(requestUpdateCompanyProductConfigs, currentUser.accessToken, action.payload.CompanyUuid, action.payload.ProductConfigs);

    const { data } = response;
    yield put(setUpdateCompanyProductConfigs(data));
  } catch (error: any) {
    yield put(setCompanyDetailError(setUpdateCompanyProductConfigsError));
  }
}

export function* handleSubmitOnboardMerchant(action: any) {
  try {
    const currentUser:IpaUserExtended= yield select(selectCurrentUser);
    const response: AxiosResponse<UserAuthResponse> = yield call(requestSubmitOnboardMerchant, currentUser.accessToken, action.payload.CompanyUuid);

    const { data } = response;
    yield put(setOnboardMerchantData(data));
  } catch (error: any) {
    yield put(setOnboardMerchantError(error?.response?.data));
  }
}

export function* portalSaga() {
  yield takeLatest(getCompanies.type, handleGetCompanies);
  yield takeLatest(getProfiles.type, handleGetProfiles);
  yield takeLatest(getCompanyDetail.type, handleGetCompanyDetail);
  yield takeLatest(getExportBankFiles.type, handleExportBankFiles);
  yield takeLatest(getUpdateCompanyProductConfigs.type, handleUpdateCompanyProductConfigs);
  yield takeLatest(submitOnboardMerchant.type, handleSubmitOnboardMerchant);
}
